import { Release } from '../../../models/release-note';

/**
 * Release notes for users, newest first. Every release in CHANGELOG.md gets
 * an entry here, written for the people working with the atlas: what changes
 * for them, no endpoints, migrations or internals. While a release is being
 * prepared on `stage`, the top entry has `version: null`; set version and
 * date when tagging (see DEV_HANDBOOK, "Releases & Versionierung").
 */
export const RELEASES: Release[] = [
  {
    version: '1.3.0',
    date: '2026-10-06',
    title: 'Eine eigene Startseite für den öffentlichen Atlas',
    items: [
      {
        type: 'new',
        audience: 'admin',
        area: 'Konfiguration',
        highlight: true,
        icon: 'fas fa-code-branch',
        title: 'Getrennte Startseiten',
        text: 'Öffentlicher und interner Atlas können jetzt unterschiedliche Kacheln auf der Startseite zeigen. In der Konfiguration „Startseite“ lassen sie sich trennen. Die öffentliche Startseite übernimmt dabei die bisher veröffentlichten Einträge. Logo, Überschrift, Karten und Diagramme bleiben gemeinsam. Mit „Trennung aufheben“ gibt es wieder eine gemeinsame Startseite.',
      },
      {
        type: 'new',
        audience: 'admin',
        area: 'Konfiguration',
        title: 'Startseite direkt im Thema festlegen',
        text: 'Bei getrennten Startseiten legen Sie im Themenformular je Atlas fest, ob ein Thema sichtbar ist und auf der Startseite steht. Die Themenliste zeigt das mit eigenen Badges.',
      },
      {
        type: 'improved',
        audience: 'all',
        area: 'Dashboard',
        title: 'Neuigkeiten neu gestaltet',
        text: 'Diese Seite zeigt jetzt verständlich, was sich mit jeder Version ändert. Gibt es Neues, weist die Karte „Neuigkeiten“ im Dashboard darauf hin.',
      },
      {
        type: 'improved',
        audience: 'admin',
        area: 'Konfiguration',
        title: 'Abbrechen ändert die Startseite nicht mehr',
        text: 'Die Startseiten-Schalter im Themenformular wirken erst beim Speichern.',
      },
      {
        type: 'fixed',
        audience: 'all',
        area: 'Öffentlicher Atlas',
        title: 'Veröffentlichte Themen erscheinen zuverlässig auf der öffentlichen Startseite',
      },
      {
        type: 'fixed',
        audience: 'admin',
        area: 'Konfiguration',
        title: 'Einstellungen der Kommune werden zuverlässiger gespeichert',
      },
    ],
  },
  {
    version: '1.2.0',
    date: '2026-10-06',
    title: 'Zusätzlicher Schutz und verlässliches Speichern',
    items: [
      {
        type: 'improved',
        audience: 'all',
        area: 'Sicherheit',
        highlight: true,
        icon: 'fas fa-shield-halved',
        title: 'Zusätzliche Sicherheitsprüfungen',
        text: 'Wir haben das Rechtekonzept geprüft und um weitere Schutzmechanismen ergänzt: Zugriffsrechte werden jetzt zusätzlich auf dem Server abgesichert. Mit Leserechten zeigt der Atlas nur noch Funktionen zum Ansehen und Exportieren.',
      },
      {
        type: 'improved',
        audience: 'all',
        area: 'Atlas',
        highlight: true,
        icon: 'fas fa-lock',
        title: 'Private Ansichten bleiben privat',
        text: 'Ihre privaten Ansichten und Themenbereiche sieht nur noch, wer sie angelegt hat. Nicht-private Ansichten können Kolleg:innen mit passenden Rechten in der Ansichten-Verwaltung bearbeiten.',
      },
      {
        type: 'new',
        audience: 'admin',
        area: 'Atlas',
        highlight: true,
        beta: true,
        icon: 'fas fa-list-check',
        title: 'Nutzung & Freigabe',
        text: 'Zeigt auf einen Blick, was eine Ansicht verwendet – Widgets, Indikatoren, Räume, Jahre und Daten – und was für die Veröffentlichung noch fehlt. „Fehlendes freigeben“ veröffentlicht es mit einem Klick. Wird pro Person im Nutzerformular freigeschaltet.',
      },
      {
        type: 'new',
        audience: 'all',
        area: 'Atlas',
        title: 'Warnung vor unvollständigen Ansichten',
        text: 'Fehlen einer Ansicht Daten oder Freigaben, weist der Atlas vor dem Veröffentlichen und beim Öffnen darauf hin.',
      },
      {
        type: 'new',
        audience: 'all',
        area: 'Atlas',
        title: 'Überarbeitetes Lesezeichen-Menü',
        text: 'Themenbereiche, die nicht mehr in die Leiste passen, finden Sie unter „Weitere Themenbereiche“.',
      },
      {
        type: 'improved',
        audience: 'all',
        area: 'Datenpflege',
        title: 'Ruhigeres Laden',
        text: 'Datenpflege und Dashboard zeigen beim Laden eine Vorschau der Seite statt eines leeren Bildschirms.',
      },
      {
        type: 'improved',
        audience: 'all',
        area: 'Datenpflege',
        title: 'Übersichtlichere Ansichten-Verwaltung',
        text: 'Viele Themen und Schlagwörter werden zu „+N“ zusammengefasst. „Kategorie“ heißt jetzt einheitlich „Themenbereich“.',
      },
      {
        type: 'improved',
        audience: 'all',
        area: 'Atlas',
        title: 'Jahresauswahl nur, wo sie gebraucht wird',
        text: 'Die Jahresauswahl erscheint nur noch in Ansichten mit Daten.',
      },
      {
        type: 'fixed',
        audience: 'all',
        area: 'Atlas',
        title: 'Widgets bleiben beim schnellen Wechsel in ihrer Ansicht',
      },
      {
        type: 'fixed',
        audience: 'all',
        area: 'Atlas',
        title: 'Verlässlicheres Speichern von Ansichten',
      },
      {
        type: 'fixed',
        audience: 'all',
        area: 'Atlas',
        title: 'Kopien einer Ansicht bekommen einen eigenen Gastlink',
      },
      {
        type: 'fixed',
        audience: 'all',
        area: 'Atlas',
        title: 'Stabiler bei langer Nutzung',
      },
      {
        type: 'fixed',
        audience: 'all',
        area: 'Öffentlicher Atlas',
        title: 'Öffentlicher Atlas auch für Eingeloggte reibungslos',
      },
      {
        type: 'fixed',
        audience: 'all',
        area: 'Atlas',
        title: 'Kleinere Korrekturen an Widgets und Overlays',
      },
      {
        type: 'improved',
        audience: 'admin',
        area: 'Sicherheit',
        title: 'Gastlinks und Arbeitsbereiche',
        text: 'Auch das Passwort von Gastlinks und die Rollen in Arbeitsbereichen werden jetzt zusätzlich auf dem Server geprüft. Gäste sehen weiterhin die vollständige Ansicht. Bitte prüfen Sie nach dem Update kurz Zugänge mit Leserechten, Arbeitsbereichs-Rollen und Gastlinks.',
      },
      {
        type: 'fixed',
        audience: 'admin',
        area: 'Konfiguration',
        title: 'Kleinere Korrekturen bei Logo und Typ-Auswahl',
      },
    ],
  },
  {
    version: '1.1.0',
    date: '2026-09-24',
    title: 'Widgets anordnen, eigenes Profil und Prognosen',
    items: [
      {
        type: 'new',
        audience: 'all',
        area: 'Atlas',
        highlight: true,
        icon: 'fas fa-grip',
        title: 'Widgets einfach anordnen',
        text: 'Über „Anordnen“ unten im Atlas sehen Sie alle Widgets einer Ansicht als Übersicht und verschieben sie per Drag & Drop. Mit Zoom, Suche, automatischem Anordnen und Rückgängig (Strg+Z) – auch bei sehr vielen Widgets. „Speichern“ liegt gleich daneben.',
      },
      {
        type: 'new',
        audience: 'all',
        area: 'Dashboard',
        highlight: true,
        icon: 'fas fa-circle-user',
        title: 'Ihr eigenes Profil mit Bild',
        text: 'Über Ihren Avatar bearbeiten Sie Name, E-Mail-Adresse und Telefonnummer selbst und laden ein Profilbild hoch. Es erscheint sofort im Avatar.',
      },
      {
        type: 'new',
        audience: 'all',
        area: 'Datenpflege',
        highlight: true,
        icon: 'fas fa-folder-tree',
        title: 'Eine Ansicht, mehrere Themenbereiche',
        text: 'Ansichten lassen sich jetzt mehreren Themenbereichen zuordnen, statt nur einem.',
      },
      {
        type: 'new',
        audience: 'all',
        area: 'Atlas',
        highlight: true,
        icon: 'fas fa-chart-line',
        title: 'Ist-Daten und Prognosen getrennt',
        text: 'Die Jahresauswahl trennt „Ist-Daten“ und „Prognose“, beide Gruppen lassen sich einzeln ein- und ausblenden. Ein Tooltip kennzeichnet Prognosejahre.',
      },
      {
        type: 'new',
        audience: 'all',
        area: 'Datenpflege',
        title: 'Warnung bei ungespeicherten Änderungen',
        text: 'Wer ein Formular mit ungespeicherten Änderungen verlässt, wird vorher gefragt.',
      },
      {
        type: 'new',
        audience: 'all',
        area: 'Atlas',
        title: 'Vorschau beim Laden',
        text: 'Startseite, Ansichten und Berichte zeigen beim Laden eine Vorschau statt eines leeren Bildschirms.',
      },
      {
        type: 'new',
        audience: 'all',
        area: 'Datenpflege',
        title: 'Bessere Auswahllisten',
        text: 'Indikatorsets lassen sich filtern, sortieren und zurücksetzen. Alle Auswahllisten für Indikatoren, Themen, Schlagwörter und Themenbereiche sehen jetzt gleich aus.',
      },
      {
        type: 'new',
        audience: 'all',
        area: 'Dashboard',
        title: 'Neuigkeiten im Dashboard',
        text: 'Was sich mit jeder Version ändert, steht jetzt direkt im Dashboard.',
      },
      {
        type: 'new',
        audience: 'admin',
        area: 'Konfiguration',
        title: 'Standard-Farbsets',
        text: 'In der Farbset-Verwaltung lassen sich Standard-Farbsets festlegen.',
      },
      {
        type: 'improved',
        audience: 'all',
        area: 'Atlas',
        title: 'Überarbeiteter Atlas',
        text: 'Kopfbereich, Lesezeichen-Menü und Validierung sind neu gestaltet. Lange Überschriften auf der Startseite brechen sauber um. Filter wirken nicht mehr in andere Ansichten hinein.',
      },
      {
        type: 'improved',
        audience: 'all',
        area: 'Datenpflege',
        title: 'Schneller startklar',
        text: 'Daten werden nur noch geladen, wenn sie gebraucht werden. Indikatoren übernehmen beim Verschieben in ein Set dessen Kategorie.',
      },
      {
        type: 'fixed',
        audience: 'all',
        area: 'Datenpflege',
        title: 'Auswahllisten behalten auch gefilterte Einträge',
      },
      {
        type: 'fixed',
        audience: 'all',
        area: 'Datenpflege',
        title: 'Listen in der Datenpflege laden vollständig',
      },
      {
        type: 'fixed',
        audience: 'all',
        area: 'Datenpflege',
        title: 'Zuverlässigerer Excel-Export',
      },
      {
        type: 'fixed',
        audience: 'all',
        area: 'Öffentlicher Atlas',
        title: 'Öffentlicher Atlas ohne unnötige Login-Hinweise',
      },
      {
        type: 'fixed',
        audience: 'all',
        area: 'Atlas',
        title: 'Widgets kommen mit Datenebenen ohne Jahre zurecht',
      },
      {
        type: 'improved',
        audience: 'all',
        area: 'Sicherheit',
        title: 'Zusätzlicher Schutz von Nutzerdaten',
        text: 'Wir haben die Sicherheitsmechanismen geprüft und erweitert. Der Zugriff auf Nutzerdaten ist jetzt zusätzlich abgesichert.',
      },
      {
        type: 'fixed',
        audience: 'admin',
        area: 'Datenpflege',
        title: 'Kleinere Korrekturen bei Lesezeichen und Indikator-Export',
      },
    ],
  },
];
